import { z } from "zod";
import { zonedParts, zonedTimeToInstant } from "./zoned-time";

/**
 * Meta writes pricing keys in two forms: `authentication-international` and
 * `free_customer_service` in status webhooks, `AUTHENTICATION_INTERNATIONAL` and
 * `FREE_CUSTOMER_SERVICE` in `pricing_analytics`. Polaris stores the lowercase
 * form with `_`, so a message and its daily bucket compare equal.
 */
export function normalizePricingKey(value: string): string {
  return value.trim().toLowerCase().replaceAll("-", "_");
}

/** Pricing of a message from a `sent` or `delivered` status (ADR 0049). */
export const statusPricingSchema = z
  .object({
    billable: z.boolean(),
    pricing_model: z.string().min(1).max(32).optional(),
    category: z.string().min(1).max(64),
    type: z.string().min(1).max(64).optional(),
  })
  .passthrough();

export type StatusPricing = {
  billable: boolean;
  pricingModel: string | null;
  category: string;
  type: string;
};

/** Normalized pricing of a status, or null when Meta sent none or an unreadable one. */
export function readStatusPricing(value: unknown): StatusPricing | null {
  const parsed = statusPricingSchema.safeParse(value);
  if (!parsed.success) return null;
  return {
    billable: parsed.data.billable,
    pricingModel: parsed.data.pricing_model ? parsed.data.pricing_model.toUpperCase() : null,
    category: normalizePricingKey(parsed.data.category),
    // Older payloads have no type: a billable one is a regular charge.
    type: normalizePricingKey(
      parsed.data.type ?? (parsed.data.billable ? "regular" : "free_customer_service"),
    ),
  };
}

/** A calendar month in UTC, the unit of the costs page and of every Meta request. */
export const costMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Mese non valido.");

export function costMonthOf(date: Date): string {
  return date.toISOString().slice(0, 7);
}

export function costMonthRange(month: string): { start: Date; end: Date } {
  const [year, monthNumber] = month.split("-").map(Number) as [number, number];
  return {
    start: new Date(Date.UTC(year, monthNumber - 1, 1)),
    end: new Date(Date.UTC(year, monthNumber, 1)),
  };
}

const pad = (value: number) => String(value).padStart(2, "0");

/** The month of an instant in the workspace's time zone. */
export function zonedCostMonthOf(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant, timeZone);
  return `${parts.year}-${pad(parts.month)}`;
}

/** A month of the workspace's time zone as instants, for the messages of the month. */
export function zonedCostMonthRange(month: string, timeZone: string): { start: Date; end: Date } {
  const [year, monthNumber] = month.split("-").map(Number) as [number, number];
  const midnightOfFirst = (y: number, m: number) =>
    zonedTimeToInstant({ year: y, month: m, day: 1, hour: 0, minute: 0 }, timeZone);
  return {
    start: midnightOfFirst(year, monthNumber),
    end: midnightOfFirst(
      monthNumber === 12 ? year + 1 : year,
      monthNumber === 12 ? 1 : monthNumber + 1,
    ),
  };
}

/**
 * The day of a Meta cost bucket. Meta's days start at midnight of the WABA's
 * time zone (22:00 UTC for an Italian number in summer), not at the start of
 * the request: the middle of the bucket falls on its day for any zone within
 * twelve hours of UTC.
 */
export function costBucketDay(periodStart: Date, periodEnd: Date): string {
  return new Date((periodStart.getTime() + periodEnd.getTime()) / 2).toISOString().slice(0, 10);
}

export function previousCostMonth(month: string): string {
  const { start } = costMonthRange(month);
  return costMonthOf(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1)));
}

/** Number status read with the costs (ADR 0049): raw Meta values, mapped by the UI. */
export const whatsappNumberStatusSchema = z
  .object({
    qualityRating: z.string().nullable(),
    messagingLimit: z.string().nullable(),
    throughputLevel: z.string().nullable(),
    checkedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type WhatsappNumberStatus = z.infer<typeof whatsappNumberStatusSchema>;

/** `metadata.costs` of a WhatsApp connection, written by the costs sync. */
export const whatsappCostsStateSchema = z
  .object({
    currency: z.string().nullable().default(null),
    lastSyncedAt: z.string().datetime({ offset: true }).nullable().default(null),
    lastErrorAt: z.string().datetime({ offset: true }).nullable().default(null),
    lastError: z.string().nullable().default(null),
    numberStatus: whatsappNumberStatusSchema.nullable().default(null),
  })
  .passthrough();
export type WhatsappCostsState = z.infer<typeof whatsappCostsStateSchema>;

export function readWhatsappCostsState(metadata: unknown): WhatsappCostsState | null {
  if (typeof metadata !== "object" || metadata === null || !("costs" in metadata)) return null;
  const parsed = whatsappCostsStateSchema.safeParse(metadata.costs);
  return parsed.success ? parsed.data : null;
}

/** Pricing of a message for owners and admins. */
export type MessagePricingDto = {
  billable: boolean;
  category: string;
  /** Estimated cost; null while Meta's daily cost of the message is not in yet. */
  cost: number | null;
  currency: string | null;
};

/** What a lead's WhatsApp messages cost, for owners and admins. */
export type LeadWhatsappCostDto = {
  billableMessages: number;
  cost: number;
  /** Billable messages still waiting for Meta's daily cost. */
  pendingMessages: number;
  currency: string | null;
};

export type WhatsappCostsDto = {
  month: string;
  /** Months with costs, newest first, always including the current one. */
  months: string[];
  currency: string | null;
  connection: {
    id: string;
    displayPhoneNumber: string | null;
    onBusinessApp: boolean;
    lastSyncedAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    numberStatus: WhatsappNumberStatus | null;
  } | null;
  totals: { cost: number; billableMessages: number; freeMessages: number };
  days: Array<{ date: string; cost: number; billableMessages: number; freeMessages: number }>;
  categories: Array<{
    category: string;
    cost: number;
    billableMessages: number;
    freeMessages: number;
  }>;
  countries: Array<{ country: string; cost: number; messages: number }>;
  leads: Array<{
    leadId: string;
    displayName: string;
    billableMessages: number;
    cost: number;
    pendingMessages: number;
  }>;
  /** Billable messages of the month still waiting for Meta's daily cost. */
  pendingMessages: number;
};
